import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Tooltip } from '@/components/ui/Tooltip';
import { SettingsSection } from '@/features/settings/settings-section';
import { cn } from '@/lib/utils';

import {
  describeMeetingRule,
  looksLikeDomain,
  meetingRuleLabel,
  parseMeetingRuleInput,
  ruleAttribution,
} from '../meeting-rule-input';
import { teamApis } from '../team.query';
import { teamStore } from '../team.store';
import { type MeetingRecordingRule, type TeamMember } from '../team.types';

import { ListEntry } from './TeamEnforcedExclusionsCard';

const RULE_GROUPS = [
  { title: 'Title contains', kinds: ['title_keyword'] },
  { title: 'Attendees', kinds: ['attendee_email', 'attendee_domain'] },
] as const;

type TeamMeetingRulesCardProps = {
  teamId: string;
  rules: MeetingRecordingRule[];
  teamDomains: string[];
  members: TeamMember[];
  currentUserEmail: string | undefined;
};

export const TeamMeetingRulesCard = observer(function TeamMeetingRulesCard({
  teamId,
  rules,
  teamDomains,
  members,
  currentUserEmail,
}: TeamMeetingRulesCardProps) {
  const [inputValue, setInputValue] = useState('');
  const [matchTitle, setMatchTitle] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const draft = parseMeetingRuleInput(inputValue, matchTitle);
  const isTeamDomain =
    draft?.kind === 'attendee_domain' &&
    teamDomains.some(
      (domain) => domain === draft.value || domain.endsWith(`.${draft.value}`)
    );
  const problem = isTeamDomain
    ? "Your team's own domain can't be excluded."
    : null;
  const canAdd = !!draft && !problem && !submitting;

  const handleAdd = async () => {
    if (!draft || !canAdd) return;
    setSubmitting(true);
    try {
      await teamApis.addMeetingRecordingRule(teamId, draft);
      setInputValue('');
      setMatchTitle(false);
    } catch {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <YStack className="w-full gap-3">
      <SettingsSection.GroupHeader>
        Excluded Meetings
      </SettingsSection.GroupHeader>
      <SettingsSection.Group>
        <SettingsSection.Item direction="vertical">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemDescription>
              Littlebird doesn't record calendar meetings that match any rule
              below, and tells members their team doesn't record meetings like
              it. Meetings without a calendar event are recorded as usual.
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>

          <YStack className="w-full gap-2 pt-4">
            <XStack className="gap-2 items-center w-full">
              <Input
                type="text"
                placeholder="Title keyword, attendee email, or domain"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setMatchTitle(false);
                }}
                className="flex-1"
                disabled={submitting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd();
                }}
              />
              <Button
                variant="outline"
                className="text-sm whitespace-nowrap"
                onClick={handleAdd}
                isLoading={submitting}
                disabled={!canAdd}
              >
                Add
              </Button>
            </XStack>

            <XStack className="min-h-5 items-baseline gap-2">
              {draft && (
                <span
                  className={cn(
                    'text-xs',
                    problem ? 'text-danger-11' : 'text-foreground-muted'
                  )}
                >
                  {problem ?? describeMeetingRule(draft)}
                </span>
              )}
              {draft && looksLikeDomain(inputValue) && (
                <Button
                  variant="link"
                  size="xs"
                  className="h-auto shrink-0 px-0 text-xs"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setMatchTitle(!matchTitle)}
                >
                  {matchTitle
                    ? 'Match attendees instead'
                    : 'Match title instead'}
                </Button>
              )}
            </XStack>
          </YStack>

          <YStack className="w-full gap-4 pt-3">
            {rules.length === 0 && (
              <p className="text-xs text-foreground-muted py-1">
                No rules yet. Meetings are recorded as usual.
              </p>
            )}
            {RULE_GROUPS.map(({ title, kinds }) => {
              const groupRules = rules.filter((rule) =>
                (kinds as readonly string[]).includes(rule.kind)
              );
              if (groupRules.length === 0) return null;
              return (
                <YStack key={title} className="w-full gap-0.5">
                  <span className="text-xs font-medium text-foreground-muted">
                    {title}
                  </span>
                  {groupRules.map((rule) => {
                    const attribution = ruleAttribution(
                      rule.added_by,
                      members,
                      currentUserEmail
                    );
                    return (
                      <Tooltip key={rule.id} label={attribution?.full ?? ''}>
                        <YStack className="w-full">
                          <ListEntry
                            label={meetingRuleLabel(rule)}
                            detail={attribution?.short}
                            isRemoving={teamStore.mutatingDomain === rule.id}
                            onRemove={() =>
                              teamApis.removeMeetingRecordingRule(
                                teamId,
                                rule.id
                              )
                            }
                          />
                        </YStack>
                      </Tooltip>
                    );
                  })}
                </YStack>
              );
            })}
          </YStack>
        </SettingsSection.Item>
      </SettingsSection.Group>
    </YStack>
  );
});
